import {produce} from 'immer';
import React, {useMemo} from 'react';
import {FeatureFlagEnum} from 'src/api/ProactApiClient';
import {ContentTitle} from 'src/design/atoms/Typography';
import {FormField} from 'src/app/forms/FormField';
import {useGetSimpleComplianceStandardListQuery} from 'src/redux/queries/complianceStandard';
import {noYesBooleanOptions} from 'src/shared/constants/fieldTypeDictionary';
import useActiveFeatureFlags from 'src/shared/hooks/useActiveFeatureFlags';
import type {ISelectOption} from 'src/types/Form';
import type {IFormFieldV2} from 'src/types/FormV2';
import {
  useGetSelectedField,
  useUpdateSelectedField,
} from '../../../hooks/useFormBuilder';

const generateEventField: IFormFieldV2 = {
  name: 'isComplianceEventField',
  label: 'Should this field generate compliance events?',
  type: 'select',
  options: noYesBooleanOptions,
};

export const ComplianceSettings = React.memo(() => {
  const field = useGetSelectedField();
  const updateSelectedField = useUpdateSelectedField();
  const activeFeatureFlags = useActiveFeatureFlags();

  const complianceEventValueOptions: ISelectOption[] = useMemo(
    () =>
      field
        ? [
            {label: field?.switchOptions?.positiveText ?? 'Yes', id: 'true'},
            {label: field?.switchOptions?.negativeText ?? 'No', id: 'false'},
          ]
        : [],
    [field?.switchOptions?.positiveText, field?.switchOptions?.negativeText]
  );

  const runQuery =
    !!field &&
    activeFeatureFlags.includes(FeatureFlagEnum.Compliance) &&
    field.type === 'switch';

  const {
    data: complianceStandardsData,
    isLoading: isComplianceStandardsLoading,
    isFetching: isComplianceStandardsFetching,
  } = useGetSimpleComplianceStandardListQuery(undefined, {skip: !runQuery});
  const options: ISelectOption[] = useMemo(() => {
    return (
      complianceStandardsData?.data?.map((item: any) => ({
        id: item.id,
        label: item.label,
      })) ?? []
    );
  }, [complianceStandardsData]);

  const eventTriggerField: IFormFieldV2 = useMemo(
    () => ({
      name: 'complianceEventTriggerValue',
      label: 'Create event when field is',
      type: 'select',
      options: complianceEventValueOptions,
      disabled: field?.builderOptions?.isComplianceEventField !== true,
    }),
    [field?.builderOptions?.isComplianceEventField, complianceEventValueOptions]
  );

  const standardsField: IFormFieldV2 = useMemo(
    () => ({
      name: 'complianceStandardIds',
      label: 'Compliance Standards',
      type: 'select-multiple',
      options,
      disabled: isComplianceStandardsLoading || isComplianceStandardsFetching,
    }),
    [options, isComplianceStandardsLoading, isComplianceStandardsFetching]
  );

  const handleIsComplianceEventFieldChange = React.useCallback(
    (name: string, value: string) => {
      if (!field) return;
      updateSelectedField(
        produce(field, draft => {
          draft.builderOptions = {
            ...draft.builderOptions,
            isComplianceEventField: value === 'true',
          };
        })
      );
    },
    [field, updateSelectedField]
  );

  const handleComplianceEventTriggerValueChange = React.useCallback(
    (name: string, value: string) => {
      if (!field) return;
      updateSelectedField(
        produce(field, draft => {
          draft.builderOptions = {
            ...draft.builderOptions,
            complianceEventTriggerValue: value,
          };
        })
      );
    },
    [field, updateSelectedField]
  );

  const handleComplianceStandardsChange = React.useCallback(
    (name: string, value: any) => {
      if (!field) return;

      updateSelectedField(
        produce(field, draft => {
          draft.builderOptions = {
            ...draft.builderOptions,
            complianceStandardIds: value,
          };
        })
      );
    },
    [field, updateSelectedField]
  );

  if (!field || field.type !== 'switch') return null;

  if (!activeFeatureFlags.includes(FeatureFlagEnum.Compliance)) return null;

  return (
    <>
      <ContentTitle text="Compliance Options" />
      <FormField
        field={generateEventField}
        value={
          field?.builderOptions?.isComplianceEventField === true
            ? 'true'
            : 'false'
        }
        onChange={handleIsComplianceEventFieldChange}
      />

      <FormField
        field={eventTriggerField}
        value={field?.builderOptions?.complianceEventTriggerValue || 'true'}
        onChange={handleComplianceEventTriggerValueChange}
      />

      <FormField
        field={standardsField}
        value={field?.builderOptions?.complianceStandardIds ?? []}
        onChange={handleComplianceStandardsChange}
      />
    </>
  );
});
